import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import LocationSelector from "../../components/ui/LocationSelector";
import ListingCard from "../../components/ui/ListingCard";
import ServiceBookingModal from "../marketplace/ServiceBookingModal";
import {
  FunnelIcon,
  MapPinIcon,
  StarIcon,
  HeartIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { useCart } from "../../contexts/CartContext";

const MarketplacePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { addItem } = useCart();
  const [bookingService, setBookingService] = useState(null);

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "all",
    type: searchParams.get("type") || "all",
    country: null,
    state: null,
    city: null,
    priceRange: "all",
    rating: "all",
    sortBy: "popular",
  });

  const [favorites, setFavorites] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMarketplaceData();
  }, []);

  const handleBookService = (service) => {
    setBookingService(service);
  };

  const fetchMarketplaceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const listingsResponse = await fetch("/api/v1/public/marketplace");
      const listingsData = await listingsResponse.json();
      if (listingsData.success) {
        setListings(listingsData.listings || []);
      } else {
        setListings([]);
      }
      const categoriesResponse = await fetch("/api/v1/public/categories");
      const categoriesData = await categoriesResponse.json();
      if (categoriesData.success) {
        setCategories(categoriesData.categories || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      setError(t("marketplace.error_unavailable"));
      setListings([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlCategory = searchParams.get("category") || "all";
    const urlType = searchParams.get("type") || "all";

    setFilters((prev) => ({
      ...prev,
      search: urlSearch,
      category: urlCategory,
      type: urlType,
    }));
  }, [searchParams]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleFavorite = (itemId) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) {
        newFavorites.delete(itemId);
      } else {
        newFavorites.add(itemId);
      }
      return newFavorites;
    });
  };

  const handleAddToCart = (item) => {
    const normalizedType = String(item?.type || "").toLowerCase();
  
    const isService =
      normalizedType === "service" ||
      normalizedType === "services" ||
      item?.advance_booking_days !== undefined ||
      item?.available_days !== undefined;
  
    if (isService) {
      setBookingService(item);
      return;
    }
  
    addItem({
      id: item.id,
      product_id: item.id,
      company_id: item.company_id,
      type: "product",
      name: item.name,
      company: item.company || item.company_name,
      price: item.price,
      description: item.description,
      image: item.image || item.image_url,
      image_url: item.image_url || item.image,
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const getListingKind = (item) => {
    const type = String(
      item?.type ||
        item?.listing_type ||
        item?.item_type ||
        item?.entity_type ||
        "",
    )
      .toLowerCase()
      .trim();

    if (type === "product" || type === "products") {
      return "product";
    }

    if (type === "service" || type === "services") {
      return "service";
    }

    const hasServiceFields =
      item?.service_id !== undefined ||
      item?.advance_booking_days !== undefined ||
      item?.assigned_employees !== undefined ||
      item?.available_days !== undefined ||
      item?.buffer_time_after !== undefined ||
      item?.buffer_time_before !== undefined ||
      item?.duration !== undefined;

    if (hasServiceFields) {
      return "service";
    }

    return "product";
  };

  const getListingDetailsPath = (item) => {
    const listingKind = getListingKind(item);

    const itemId =
      listingKind === "service"
        ? item?.service_id || item?.id
        : item?.product_id || item?.id;

    if (!itemId) {
      return null;
    }

    if (listingKind === "service") {
      return `/marketplace/services/${itemId}`;
    }

    return `/marketplace/products/${itemId}`;
  };

  const filteredListings = listings.filter((item) => {
    // 1. Фильтр по категории
    if (filters.category !== "all" && item.category !== filters.category)
      return false;

    // 2. Фильтр по типу (товары/услуги)
    if (
      filters.type !== "all" &&
      filters.type !== "products" &&
      filters.type !== "services"
    ) {
      if (item.type && item.type !== filters.type) return false;
    }

    // 3. Текстовый поиск (имя, описание, компания)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        item.name.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        (item.company && item.company.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;
    }

    // 4. Фильтр по локации (из LocationSelector)
    // Приоритет: город -> если города нет, проверяем штат -> если ничего не выбрано, пропускаем
    const selectedLocationName = filters.city?.name || filters.state?.name;

    if (selectedLocationName) {
      if (
        !item.location ||
        !item.location
          .toLowerCase()
          .includes(selectedLocationName.toLowerCase())
      ) {
        return false;
      }
    }

    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-bold text-zinc-500">
            {t("marketplace.loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20 pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Location, Filters & Categories */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="sticky top-8 space-y-4">
              {/* Location Selector Group */}
              <div className="bg-white rounded-[28px] border border-zinc-100 p-4 shadow-sm space-y-3">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 ml-2">
                    {t("marketplace.sidebar.location")}
                  </label>
                  <LocationSelector
                    selectedCountry={filters.country}
                    selectedState={filters.state}
                    selectedCity={filters.city}
                    onLocationChange={(loc) =>
                      setFilters((p) => ({ ...p, ...loc }))
                    }
                  />
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`w-full flex items-center justify-center space-x-2 py-3 rounded-2xl font-black text-sm transition-all ${
                    showFilters
                      ? "bg-zinc-900 text-white shadow-lg"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span>
                    {showFilters
                      ? t("marketplace.sidebar.filters_btn_hide")
                      : t("marketplace.sidebar.filters_btn_show")}
                  </span>
                </button>

                {/* Extended Filters Panel in Sidebar */}
                {showFilters && (
                  <div className="pt-4 border-t border-zinc-100 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    {[
                      // {
                      //   label: t("marketplace.filters.type_label"), key: 'type', options: [
                      //     { val: 'all', text: t("marketplace.filters.types.all") },
                      //     { val: 'services', text: t("marketplace.filters.types.services") },
                      //     { val: 'products', text: t("marketplace.filters.types.products") }
                      //   ]
                      // },
                      {
                        label: t("marketplace.filters.sort_label"),
                        key: "sortBy",
                        options: [
                          {
                            val: "popular",
                            text: t("marketplace.filters.sort.popular"),
                          },
                          {
                            val: "price-low",
                            text: t("marketplace.filters.sort.price_low"),
                          },
                          {
                            val: "price-high",
                            text: t("marketplace.filters.sort.price_high"),
                          },
                        ],
                      },
                    ].map((filterGroup) => (
                      <div key={filterGroup.key} className="space-y-1.5">
                        <label className="block text-[10px] font-black uppercase text-zinc-400 ml-1">
                          {filterGroup.label}
                        </label>
                        <select
                          value={filters[filterGroup.key]}
                          onChange={(e) =>
                            handleFilterChange(filterGroup.key, e.target.value)
                          }
                          className="w-full bg-zinc-50 border-none rounded-xl p-3 text-xs font-bold text-zinc-900 focus:ring-2 focus:ring-orange-500 appearance-none cursor-pointer"
                        >
                          {filterGroup.options.map((opt) => (
                            <option key={opt.val} value={opt.val}>
                              {opt.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Categories Navigation */}
              <div className="bg-white rounded-[28px] border border-zinc-100 p-3 shadow-sm">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 px-4 pt-3 pb-2">
                  {t("marketplace.sidebar.categories_title")}
                </h3>
                <nav className="space-y-1">
                  <button
                    onClick={() => handleFilterChange("category", "all")}
                    className={`w-full text-left px-4 py-3 rounded-2xl text-sm font-bold transition-all flex justify-between items-center ${
                      filters.category === "all"
                        ? "bg-zinc-900 text-white shadow-md"
                        : "text-zinc-600 hover:bg-zinc-50"
                    }`}
                  >
                    <span>{t("marketplace.sidebar.all_categories")}</span>
                    <span className="opacity-50 text-xs">
                      {listings.length}
                    </span>
                  </button>

                  {categories.map((category) => {
                    const count = listings.filter(
                      (item) => item.category === category.id,
                    ).length;
                    return (
                      <button
                        key={category.id}
                        onClick={() =>
                          handleFilterChange("category", category.id)
                        }
                        className={`w-full text-left px-4 py-3 rounded-2xl text-sm font-bold transition-all flex justify-between items-center ${
                          filters.category === category.id
                            ? "bg-zinc-900 text-white shadow-md"
                            : "text-zinc-600 hover:bg-zinc-50"
                        }`}
                      >
                        <span>{category.name}</span>
                        <span className="opacity-50 text-xs">{count}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Results Grid */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-black text-zinc-900 tracking-tight">
                  {filters.search
                    ? `${t("marketplace.search_results")} "${filters.search}"`
                    : t("marketplace.title")}
                </h1>
                <p className="text-zinc-500 font-medium mt-1">
                  {t("marketplace.found_options")}{" "}
                  <span className="text-zinc-900 font-bold">
                    {filteredListings.length}
                  </span>
                </p>
              </div>
            </div>

            {filteredListings.length === 0 ? (
              <div className="p-16 text-center bg-white rounded-[40px] border-2 border-dashed border-zinc-200">
                <MagnifyingGlassIcon className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-xl font-black text-zinc-900 mb-2">
                  {t("marketplace.empty.title")}
                </h3>
                <p className="text-zinc-500 font-medium">
                  {t("marketplace.empty.description")}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredListings.map((item) => {
                  const listingKind = getListingKind(item);
                  const detailsPath = getListingDetailsPath(item);

                  return (
                    <ListingCard
                      key={`${listingKind}-${item.id}`}
                      item={{
                        ...item,
                        type: listingKind,
                      }}
                      isFavorite={favorites.has(item.id)}
                      onToggleFavorite={toggleFavorite}
                      onAddToCart={handleAddToCart}
                      onBookService={handleBookService}
                      detailsPath={detailsPath}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <ServiceBookingModal
        service={bookingService}
        isOpen={Boolean(bookingService)}
        onClose={() => setBookingService(null)}
        onSuccess={() => {
          setBookingService(null);
        }}
      />
    </div>
  );
};

export default MarketplacePage;
